import CustomInput from "@components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import { UserNameFields } from "./username.types";
import { useDispatch, useSelector } from "react-redux";
import LoadingAnimation from "assets/svg/LoadingAnimation";
import { useSnackbar } from "notistack";
import { setUserData } from "store/actions/auth.action";
import { updateUserName } from "endpoints/auth.service";

const Username: React.FC = () => {
  const { user } = useSelector((state: any) => state.auth);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    setValue,
  } = useForm<UserNameFields>({
    defaultValues: {
      userName: user.userName,
      isEditing: false,
      isLoading: false,
    },
  });

  const fields = watch();

  const usernameValidationRules = {
    required: "Username is required",
    minLength: {
      value: 5,
      message: "Username must be at least 5 characters long",
    },
    pattern: {
      value: /^[a-zA-Z0-9_]+$/,
      message: "Username can only contain letters, numbers, and underscores",
    },
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied to clipboard", { variant: "success" });
  };

  const handleUpdateUsername = async (data) => {
    try {
      setValue("isLoading", true);
      const newUsername = data?.userName;

      const success = await updateUserName({ userName: newUsername });

      if (success) {
        setValue("userName", newUsername);
        dispatch(
          setUserData({
            ...user,
            userName: newUsername,
          })
        );
        enqueueSnackbar("Username updated successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update personal details", {
        variant: "error",
      });
    } finally {
      setValue("isLoading", false);
      setValue("isEditing", false);
    }
  };

  return (
    <div className="flex-1 bg-white rounded-md text-xs h-fit">
      <p className="text-sm font-semibold mb-1">Username</p>
      <div className="my-4 flex items-center">
        <div className="flex-grow">
          {/* @ts-ignore */}
          <CustomInput
            name="userName"
            rules={usernameValidationRules}
            control={control}
            errors={errors}
            disabled={!fields?.isEditing || fields?.isLoading}
          />
        </div>
        <button
          className="text-xs text-charcoal ml-5 h-9 flex items-center w-1/12"
          onClick={() => {
            if (!fields?.isEditing) {
              setValue("isEditing", true);
              trigger("isEditing");
              return;
            } else {
              handleSubmit(handleUpdateUsername)();
            }
          }}
          disabled={fields?.isLoading}
        >
          {fields?.isLoading ? (
            <LoadingAnimation color={"black"} />
          ) : fields?.isEditing ? (
            "Update"
          ) : (
            "Change"
          )}
        </button>
      </div>
      <div className="flex items-center">
        <div className="py-2.5 px-3 rounded-lg bg-light-white-700 border border-lightgray font-montserrat font-medium text-xs flex-grow">
          {`https://comtracked/profile/${watch("userName")}`}
        </div>
        <button
          className="text-xs text-charcoal ml-5 h-9 flex items-center w-1/12"
          onClick={() =>
            copyToClipboard(`https://comtracked/profile/${watch("userName")}`)
          }
        >
          Copy
        </button>
      </div>
    </div>
  );
};

export default Username;
